"use client";

import { useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";

import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Hamburger } from "@/components/Hamburger";
import { Wrapper } from "@/components/Wrapper";
import { Logo } from "@/components/Logo";

import { getSiteInfo } from "@/sites";

import { isClient } from "@/utils/browser";
import { paths } from "@/utils/paths";

import { AMPLITUDE_EVENT_NAME } from "@/internals/constants/events";
import { AUTHORIZATION } from "@/internals/constants/cookies";

import { LayoutContext } from "@/contexts/LayoutContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";

import { useLogin } from "@/hooks/auth/useLogin";
import { useDomainConfig } from "@/hooks/domain/useDomainConfig";
import { useWindowSize } from "@/hooks/browser";

import { HeaderProps } from "./types";
import { NAMESPACES } from "@/internals/constants/namespaces";

const { siteName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);
const { siteBlackLogo28 } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};

export const Header = ({ noNav }: HeaderProps) => {
  const { t } = useTranslation(NAMESPACES.LAYOUT);
  const layoutContext = useContext(LayoutContext);
  const { setSource } = useContext(AmplitudeContext);

  const { isUserLoggedIn } = useLogin();
  const { logoLinkEnabled } = useDomainConfig();
  const [cookies] = useCookies();
  const screenSize = useWindowSize();
  const router = useRouter();

  const [isRegisterPage, setIsRegisterPage] = useState(false);
  const [isUserLogged, setIsUserLogged] = useState(false);

  const location = isClient() ? window.location : undefined;

  const navigateTo = (path: string) => {
    router.push(path);
    layoutContext?.setIsMenuOpened(false);
  };

  useEffect(() => {
    setIsRegisterPage(location?.pathname.includes("register"));
    const currentLoginStatus = Boolean(isUserLoggedIn());
    setIsUserLogged(currentLoginStatus);
  }, [location?.pathname]);

  useEffect(() => {
    const hasAuthCookie = Boolean(cookies?.[AUTHORIZATION]);
    setIsUserLogged(hasAuthCookie);
  }, [cookies]);

  const overFlowMenuClass = () => {
    const bodyElement = document.querySelector("body");
    const htmlElement = document.querySelector("html");
    bodyElement.classList.remove("overflowHidden");
    htmlElement.classList.remove("overflowHidden");
  };

  const handleClickMenu = (event, navPath) => {
    overFlowMenuClass();

    if (event) {
      amplitudeTrackEvent(AMPLITUDE_EVENT_NAME[event], {
        source: "navbar",
      });
    }
    navigateTo(navPath);
  };

  const handlePrefetch = (path: string) => {
    router.prefetch(path);
  };

  return (
    <header
      data-testid="header"
      className="header"
    >
      <Wrapper customClass="header__wrapper">
        <Logo
          href={logoLinkEnabled && "/"}
          src={siteBlackLogo28}
          siteName={siteName}
          dataQa="header-logo-link"
          dataTestId="header-logo"
          className="header__logo"
          setSource={setSource}
          width={189}
          height={28}
          loading="eager"
          quality={65}
        />

        {!noNav && (
          <div className="nav__wrapper">
            {!isRegisterPage && (
              <Hamburger>
                <div className="header-nav__language-selector">
                  <LanguageSelector
                    fullWidth
                    upperDropdown={screenSize?.isMobile}
                    ariaLabel="Change site language"
                    dataQa="header-language-selector"
                  />
                </div>
                {isUserLogged ? (
                  <ul className="header-nav__actions">
                    <li
                      className="header-nav__actions-item"
                      onMouseEnter={() => handlePrefetch(paths.myQrCodes())}
                    >
                      <ButtonLib
                        shape="outlined"
                        label={t("header_button_dashboard_label")}
                        dataQA="header-dashboard-button"
                        onClick={() => handleClickMenu(undefined, paths.myQrCodes())}
                      />
                    </li>
                  </ul>
                ) : (
                  <ul className="header-nav__actions">
                    <li
                      className="header-nav__actions-item"
                      onMouseEnter={() => handlePrefetch(paths.login())}
                    >
                      <ButtonLib
                        shape="ghost"
                        id="login"
                        label={t("header_button_login_label")}
                        dataQA="header-login-button"
                        onClick={() => handleClickMenu("view_landing_login", paths.login())}
                      />
                    </li>
                    <li
                      className="header-nav__actions-item"
                      onMouseEnter={() => handlePrefetch(paths.signup())}
                    >
                      <ButtonLib
                        label={t("header_button_signup_label")}
                        shape="outlined"
                        dataQA="header-sign-up-button"
                        onClick={() => handleClickMenu("view_landing_signup", paths.signup())}
                      />
                    </li>
                  </ul>
                )}
              </Hamburger>
            )}
          </div>
        )}
      </Wrapper>
    </header>
  );
};
